import { useCallback, useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';

// One key for every company: reading a new company's perimeters cancels the
// request for the previous one, and never the company panel's own lists.
const MISSION_PERIMETERS_ABORT_KEY = 'admin/missionCreation/perimeters';

const IDLE = { status: 'idle', perimeters: [] };

// The active perimeters of the company a mission is created for, and the one
// chosen for it (null = « Aucun périmètre », the default).
const useMissionPerimeterField = (idCompany) => {
  const [state, setState] = useState(IDLE);
  const [perimeterId, setPerimeterId] = useState(null);
  // Only the latest request may fill the field.
  const latestRequest = useRef(null);

  const load = useCallback(() => {
    if (!idCompany) return;
    const request = {};
    latestRequest.current = request;
    ApiDatabase.getAdminCompanyPerimeters(
      { idCompany, status: 'active' },
      (answer) => {
        if (latestRequest.current !== request) return;
        const perimeters = Array.isArray(answer?.data) ? answer.data : [];
        setState({ status: 'ready', perimeters });
        // A perimeter archived or deleted since it was chosen falls back to
        // « Aucun » rather than staying selected out of the list.
        setPerimeterId((current) =>
          perimeters.some((perimeter) => perimeter.id === current)
            ? current
            : null,
        );
      },
      (err) => {
        if (latestRequest.current !== request) return;
        if (err?.name === 'CanceledError') return;
        console.error(err);
        setState({ status: 'error', perimeters: [] });
      },
      { abortKey: MISSION_PERIMETERS_ABORT_KEY },
    );
  }, [idCompany]);

  // Another company: its own perimeters, and the default choice again.
  useEffect(() => {
    setPerimeterId(null);
    setState(idCompany ? { status: 'loading', perimeters: [] } : IDLE);
    load();
    return () => {
      latestRequest.current = null;
    };
  }, [idCompany, load]);

  // A reload keeps the current list on screen until the answer comes.
  return { ...state, perimeterId, setPerimeterId, reload: load };
};

export default useMissionPerimeterField;
